import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import Badge from '@repo/ui/badge'
import { CoinIcon, InformationIcon } from '@repo/ui/icons'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'

import { getActivities } from '../../../../../api/ols/activities/get-activities'
import useTranslate from '../../../../../lib/hooks/use-translate'
import {
  OutingDurationEnum,
  outingDurationEnumMapping
} from '../../../../../lib/types/enums/cultural-outing/outing-duration.enum'
import { CulturalOutingInput } from '../../../../../lib/types/models/cultural-outing/cultural-outing-input'
import { BaseFormProps } from '../../../../../lib/types/utils'
import { getSubmitAction } from '../../../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../../../lib/utils/functions/misc.functions'
import { FORM_MODE } from '../../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../../elements/forms/form'
import ControlledDateStringInput from '../../../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../../elements/validation-buttons'
import { CULTURAL_OUTING_SCHEMA_VALIDATION } from '../../utils'

type Props = BaseFormProps<CulturalOutingInput> & {
  olId: number
  className?: string
}

const CulturalOutingForm = ({
  defaultValues,
  className,
  olId,
  ...validationButtonProps
}: Props) => {
  const { currentLanguage } = useTranslate()
  const form = useForm<CulturalOutingInput>({
    resolver: zodResolver(CULTURAL_OUTING_SCHEMA_VALIDATION(currentLanguage)),
    defaultValues,
    mode: FORM_MODE
  })
  const { t } = useTranslate()

  const { data: activities } = useQuery({
    queryKey: ['activities'],
    queryFn: () => getActivities({ olId })
  })
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={'divide-y divide-white-active'}>
          <div className={cn('flex flex-col gap-3', className)}>
            <Badge
              iconPosition="start"
              size="medium"
              color={tailwindColors.secondary.light.DEFAULT}
              textColor={tailwindColors.secondary.DEFAULT}
              className="text-nowrap"
              icon={<InformationIcon color={tailwindColors.secondary.DEFAULT} />}
              text="Reminder : first create your cultural outing / activity in the activity menu to be able to select it here"
            />
            <FormField
              control={form.control}
              name="activityId"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      required
                      disabled={!activities?.data.length}
                      label={'Select the cultural outing / activity'}
                      options={activities?.data.map((activity) => ({
                        label: activity.name,
                        value: activity.id
                      }))}
                      selectedOption={conditionalRender(!!field.value, {
                        label:
                          activities?.data.find((activity) => activity.id === field.value)?.name ??
                          '-',
                        value: field.value
                      })}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="numberOfSeats"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput required {...field} label={'# of seats'} type={'number'} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="duration"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      required
                      label={'Duration'}
                      options={Object.values(OutingDurationEnum).map((duration) => ({
                        label: t(outingDurationEnumMapping[duration]),
                        value: duration
                      }))}
                      selectedOption={getSelectedEnumOption(
                        field.value,
                        outingDurationEnumMapping,
                        t
                      )}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cost"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput
                      required
                      {...field}
                      icon={CoinIcon}
                      label={'Cost'}
                      type={'number'}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="validUntil"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledDateStringInput {...field} mode="over" label={'Valid until'} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="comment"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput {...field} label={'Comment'} type={'textarea'} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="pb-6 px-4">
            <ValidationButtons {...validationButtonProps} />
          </div>
        </div>
      </form>
    </Form>
  )
}

export default CulturalOutingForm
